import * as THREE from 'three';
import { TAU } from '../core/math';
import { MECHS } from '../entities/mechs';
import { buildMechFX } from '../render/mech-fx';
import { skillEls } from '../platform/dom';
import { G, mesh, scene } from '../render/scene';
import { buildBeam } from '../render/beam-fx';
/* ---------- 玩家狀態 ---------- */
export type Stats = ReturnType<typeof baseStats>;

export var P = {
  mech: null as any, rig: null as any,
  pos: new THREE.Vector3(), vel: new THREE.Vector3(),
  prevX: 0, prevZ: 0,      // 上一個模擬步的位置，供渲染內插
  viewX: 0, viewZ: 0,      // 內插後的視覺位置，鏡頭跟這個而不是 pos
  faceMove: 0, faceAim: 0,
  hp: 100, hpMax: 100, sh: 40, shMax: 40, shTimer: 0,
  /* 開箱的**待選**狀態（2026-08-04 晚：拉霸拔掉，改回三選一／五選三）。
     `ids` 是這一箱給的選項、`picks` 是還要選幾次、`tier` 給標題用。

     與拉霸版的關鍵差別：那時候東西在撿起來就套用完了（`chestGot` 是「已經拿到」），
     現在是**還沒套用**——玩家選了才 `take()`。三選一不需要「先套用再顯示」那道
     保險，因為介面會暫停模擬，選完之前玩家不會死。

     用「模擬層存狀態、`game.ts` 的模擬步開介面」這條既有路徑，
     而不是讓 sim 去 import ui。 */
  chestOffer: [] as string[], chestPicks: 0, chestTier: '',
  /** 還有幾次機體武裝強化三選一沒領（`data/mech-upgrades.ts`），
      跟一般升級走同一個介面但**不吃 pendingLv**。

      ⚠️ 2026-08-04 晚之後 **Boss 擊破不再走這裡**（改成掉金箱，
      第一格保證是機體強化）。現在唯一的來源是精英首領小機率掉的
      橘色武裝強化箱（`MW_DROP_CHANCE`）。 */
  bossUp: 0,
  scrap: 0, kills: 0, revives: 1,   // scrap 現在是跨場的局外解鎖貨幣（GDD §11）
  /* 經驗與等級（M4 裝回來）。pendingLv 是佇列 —— 一次拾取可能跳好幾級，
     每一級都要讓玩家選一次，不能吃掉。 */
  lv: 1, xp: 0, xpNeed: 8, pendingLv: 0,
  up: {} as Record<string, number>,   // 已取得的強化 → 等級
  iframe: 0, dashT: 0, dashDir: new THREE.Vector3(),
  /* 推進器飛行（規格：`.claude/notes/flight-spec.md`）。
     `y` 是模擬層的**世界高度**（不是離地高度），`viewY` 是內插後給渲染用的。
     ⚠️ 落地判定一律問 `groundYAt(x,z)`，不要寫死 0 —— 地圖有可站立平台之後
     「地面」在不同位置是不同高度的。`gy` 是這一步查出來的腳下地面高度，
     每步只查一次（`sim/flight.ts`），其他人一律讀它。 */
  y: 0, gy: 0, vy: 0, viewY: 0, prevY: 0, apexY: 0,
  /** 推進能量。與衝刺充能**各自獨立**（飛行不吃掉閃避，否則沒人敢飛）。 */
  thrust: 100, thrustCd: 0, flying: false,
  /** 這一場的滯空累計秒數。存在的理由是反制夠不夠只能用它來量，見規格檔。 */
  airT: 0,
  /** 空中的輸出倍率（由 `sim/flight.ts` 每步更新，`damageEnemy` 讀它）。
      放在 `P` 上而不是讓 `fx.ts` import `flight.ts` —— flight 已經 import 了
      fx 的 `areaDamage`，反向再 import 就是循環相依。 */
  airMul: 1,
  fireCd: 0, midCd: 0, ultCd: 0, ultT: -1, barrel: 0, walkPhase: 0, swing: 0,
  /* 近戰連段（LANCER-2 的普攻，見 `.claude/notes/lancer2-arts.md` §1）。
     `combo` 0/1/2 = 這一刀是第幾段，`comboT` 是窗口剩餘秒數。
     **打不到人也算一段** —— 敵人被擊退之後就打不到，不算的話第三段永遠出不來。 */
  combo: 0, comboT: 0,
  /* 旋刃的成長量 0→1（`updateVortex`）。命中越多轉越快、範圍越大、收束越猛。
     純上行、不打人只是沒長大，**沒有任何懲罰** —— 累積型的懲罰儀表在這個
     專案被否決過（lessons 2026-08-01 的熱量系統）。 */
  vortexSpin: 0, vortexBeat: 0,
  lockT: null as any, lockOn: false, god: false, alive: true,   // god 只由除錯主控台切換
  dashAttack: false, ultTarget: null as any, slashHits: null as any,
  /* 規則類被動用的狀態（見 sim/rules.ts）。
     `stillT` 靜止多久（磁軌校準）、`ghost*` 衝刺殘影的引爆點與倒數。 */
  stillT: 0, ghostX: 0, ghostZ: 0, ghostT: 0,
  mod: { primary: null as string | null, mid: null as string | null, ult: null as string | null },
  /* base 是機體的原始數值，s 是套用被動之後的結果。
     兩層的理由見 sim/stats.ts —— 直接改 s 會讓「原始值 vs 加成後」消失，
     而且同一項升兩級會變成乘兩次當下的值。 */
  base: null as Stats | null,
  s: {} as Stats
};

/* 統計軸。新增的八條見 `.claude/notes/upgrade-redesign.md` §3。

   加軸的原則（違反就是死碼或假選擇）：
     **每一條都要有至少一個被動去動它，而且要有至少一把武裝吃它。**
   沒有人動的軸是死碼；沒有武裝吃的軸是玩家看得到但感覺不到的假選擇。 */
export function baseStats(mech) {
  var st = mech.stats;
  return {
    speed: mech.speed, atkDmg: st.atkDmg, atkRate: st.atkRate, atkRange: st.atkRange,
    atkCount: st.atkCount, atkSpeed: st.atkSpeed,
    midDmg: st.midDmg, midCount: st.midCount, midCdMax: st.midCdMax,
    ultDmg: st.ultDmg, ultCdMax: st.ultCdMax, ultDur: st.ultDur,
    crit: 0.05, critMul: 2.0, magnet: 6, shardKill: 0, lifesteal: 0,
    /* 幸運：**不影響任何傷害**。兩個出口 —— 寶箱的池子大小（`sim/chest.ts`，
       上限 +2）與抽卡時史詩／傳說的權重（`data/rarity.ts`）。
       （2026-08-04 晚拉霸拔掉之前它影響的是「一箱開幾件」。）
       `05-meta-progression.md` 點名它是「體感強但實際弱」的代表 ——
       局外預算花在這種屬性上，才不會把永久強化的總戰力增益推過 30% 的硬上限。 */
    luck: 1,
    /* 對齊 VS 的 Area / Duration —— 這兩條是 VS 裡讓同一把武器
       「覆蓋形狀完全不同」的主要來源，我們原本整個沒有。 */
    area: 1, dur: 1,
    /* **衝擊軸：這是我們與 VS 的差異點。**
       VS 只有「清場」與「活著」兩條軸，我們多一條「打硬目標」。
       它與密度是反向的 —— 清場 build 打不動 Boss，衝擊 build 面對雜魚海很慢。
       那個反向關係就是 build 分化，也是重玩的理由。 */
    imp: 1, stagT: 1, stagDmg: 1,
    /* 生存與節奏。armor 是**乘算**（受傷 ×= armor），所以永遠到不了 0；
       線性減免疊到 100% 就無敵了，那是這類詞條最典型的死法。 */
    armor: 1, regen: 0, growth: 1,
    /* 推進器五條軸（使用者拍板的四個成長方向 + 落地衝擊拆成傷害與範圍）。
       每一條都有配件或被動在動它 —— 沒有人動的軸是死碼（見上面的加軸原則）。
       `airDmg` 是**空中的輸出倍率**：預設 0.7 就是「空中打得到但打得輕」，
       強化到 1 等於買回懲罰，這是「空中戰 build」的入口。 */
    /* `thrustRegen` 的沿革：40 → 9 → 6（使用者 2026-08-03 兩次「恢復再慢一些」）
       → **12**（2026-08-06「恢復速度改成現在的兩倍」）。

       翻倍的理由是這條能量**現在同時餵衝刺**（2026-08-06 併軌）：
       6 的時候從空條回到能衝一次要 4.6 秒、回到能起飛要 9 秒 ——
       那是為「飛行是爆發」訂的節流，套在閃避上會變成「大部分時間不能閃」。
       12 之後：能衝一次約 2.3 秒、能起飛約 4.4 秒（地面回充 ×1.5）。 */
    thrustMax: mech.thrustMax, thrustRise: mech.thrustRise, thrustRegen: 12,
    airDmg: 0.7, slamDmg: 40, slamArea: 1
  };
}

export function equipMech(id) {
  var m = MECHS[id];
  if (P.rig) scene.remove(P.rig.root);
  P.mech = m; P.rig = m.build();
  /* 只有機體投影 —— 敵人走 InstancedMesh 且有 300 隻，開了會讓
     shadow map 每幀重繪全場（見 platform/renderer.ts）。 */
  P.rig.root.traverse(function (o: any) { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
  /* 特效**一定要在 `mergeBones()` 之後**才掛（`buildChassis0` 尾端已經合併過）。
     噴焰與槍口閃光每幀改 scale/opacity，被烘進靜態幾何就再也動不了，
     而且不會有任何錯誤 —— 只會看到一團永遠不動的橘色錐體。 */
  if (P.rig.kind === 'v3') buildMechFX(P.rig);
  scene.add(P.rig.root);
  P.base = baseStats(m); P.s = baseStats(m);
  P.hpMax = m.hp; P.hp = m.hp; P.shMax = m.sh; P.sh = m.sh;
  /* 能量**預設滿的**（使用者 2026-08-06）：開場就有一次衝刺或一次起飛，
     不用先站著等回充 —— 開場的三十秒本來就是這個遊戲最無聊的一段。 */
  P.thrust = m.thrustMax; P.thrustCd = 0;
  /* **開場飛行條是空的**（使用者指定）。第一分鐘是教走位與地面威脅的，
     一開局就能飛等於把那一課跳過去。 */
  P.thrust = 0; P.y = 0; P.vy = 0; P.viewY = 0; P.prevY = 0; P.flying = false; P.airT = 0;
  P.mod = { primary: null, mid: null, ult: null };
  skillEls.dash.ico.textContent = '衝刺';
  skillEls.mid.ico.textContent = m.icons[1];
  skillEls.ult.ico.textContent = m.icons[2];
}

/* 大招光束（CHASSIS-0）。造型與 shader 在 `render/beam-fx.ts` ——
   舊版是三層 BoxGeometry 疊加，每一格像素同一個值，所以讀成一塊白板子
   而不是能量。改版後的實拍見 `.claude/notes/shots/wfx/03-ult-b.png`。 */
export var beamPivot = new THREE.Group(), BEAM_LEN = 44;
(function () {
  var rig = buildBeam(BEAM_LEN, 2.3, 0xc4dcf4, 0x3f8fd8);
  beamPivot.add(rig.group);
  beamPivot.visible = false; beamPivot.userData = { rig: rig, tick: 0 };
  scene.add(beamPivot);
})();

/* 旋刃場（LANCER-2） */
export var vortexRig = new THREE.Group();
(function () {
  var m = new THREE.MeshBasicMaterial({ color: 0x9fffe8, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false });
  for (var i = 0; i < 4; i++) {
    var a = (i / 4) * TAU;
    var b = mesh(G.box, m, [0.3, 0.5, 6.4], [Math.sin(a) * 3.2, 1.8, Math.cos(a) * 3.2], [0, a + Math.PI / 2, 0]);
    vortexRig.add(b);
  }
  var ring = new THREE.Mesh(G.ring, new THREE.MeshBasicMaterial({ color: 0x9fffe8, transparent: true, opacity: 0.28, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
  ring.rotation.x = -Math.PI / 2; ring.position.y = 0.12; ring.scale.setScalar(7);
  vortexRig.add(ring); vortexRig.userData = { ring: ring, mat: m };
  vortexRig.visible = false; scene.add(vortexRig);
})();

/* 斬擊軌跡（LANCER-2） */
export var slashFx = new THREE.Group();
(function () {
  var m = new THREE.MeshBasicMaterial({ color: 0xbfffef, transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
  var r = new THREE.Mesh(new THREE.RingGeometry(0.45, 1, 26, 1, -1.05, 2.1), m);
  r.rotation.x = -Math.PI / 2; slashFx.add(r);
  slashFx.userData = { mat: m, life: 0 }; slashFx.visible = false; scene.add(slashFx);
})();

/** 把地面斬擊扇形對準世界角度 `a`（與 `P.faceAim` 同一個約定：
    方向 = `(sin a, 0, cos a)`）。

    **一定要走這個函式，不要自己寫 rotation.y。** 這裡有兩層座標轉換疊在一起：
    `RingGeometry` 躺在 XY 平面、角度從 +X 量向 +Y，而那片環被 `rotation.x = -π/2`
    放平 —— 放平之後環的 +Y 指到世界的 **−Z**。所以「環的 0 度」對到世界 +X，
    要讓它轉到 `(sin a, 0, cos a)` 得轉 `a − π/2`。

    舊版四個呼叫點全都寫成 `−a + π/2`（正好差一個負號）。那個錯是**鏡射**
    不是偏移：南北顛倒、東西不變 —— 所以往東西打的時候完全看不出來，
    往南北打就整片反過來（使用者 2026-08-06 回報「顯示往南其實往北」）。
    `off` 是額外的偏轉（收刀那一拍要橫過身體，給 π/2）。 */
export function aimSlashFx(a, off?) { slashFx.rotation.y = a - Math.PI / 2 + (off || 0); }
